import {Injectable} from '@angular/core';
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {Observable} from "rxjs";
import {CheckVetineoConfigurationResponse} from "../models/check-vetineo-configuration-response.model";
import {VetineoConfigurationError, VetineoConfigurationErrorDto} from "../models/vetineo-configuration-error.model";

/**
 * Serwis warstwy danych zarządzający synchronizacją konfiguracji Vetineo.
 * Odpowiada za weryfikację gotowości operacyjnej kliniki do przyjmowania rezerwacji online
 * oraz za wymuszanie propagacji ustawień i harmonogramów do systemów Sitesbi i KlinikaXP
 */
@Injectable({
  providedIn: 'root'
})
export class VetineoConfigurationService {

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils,
  ) {
  }

  /**
   * Wykonuje kompleksowy audyt konfiguracji kliniki.
   * Weryfikuje, czy placówka poprawnie zmapowała lekarzy, typy wizyt i godziny pracy,
   * aby bezpiecznie uruchomić rezerwacje online.
   *
   * @param clinicUUID Unikalny identyfikator kliniki.
   * @param withWidget Flaga wymuszająca dodatkową weryfikację konfiguracji widgetu WWW.
   * @returns Observable emitujący szczegółowy raport z audytu konfiguracji.
   */
  checkVetineoConfiguration(clinicUUID: string, withWidget: boolean): Observable<CheckVetineoConfigurationResponse> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/check-vetineo-configuration/${clinicUUID}/${withWidget}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<CheckVetineoConfigurationResponse>(new CheckVetineoConfigurationResponse(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wymusza synchronizację głównych ustawień
   * lecznicy z Vetineo do Sitesbi
   *
   * @param clinicUUID Unikalny identyfikator kliniki.
   * @param withWidget Flaga decydująca o dołączeniu konfiguracji widgetu do paczki synchronizacyjnej.
   * @returns Observable emitujący obiekt DTO zawierający ewentualne błędy mapowania/synchronizacji.
   */
  setVetineoSettingsInSitesbi(clinicUUID: string, withWidget: boolean): Observable<VetineoConfigurationErrorDto> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/set-vetineo-configuration/${clinicUUID}/${withWidget}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new VetineoConfigurationErrorDto(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wymusza synchronizację siatki kalendarza/harmonogramu pracy z Vetineo do Sitesbi
   *
   * @param clinicUUID Unikalny identyfikator kliniki.
   * @returns Observable emitujący tablicę błędów, jeśli określone bloki czasowe nie mogły zostać zsynchronizowane.
   */
  setVetineoGridInSitesbi(clinicUUID: string): Observable<VetineoConfigurationError[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/set-vetineo-grid/${clinicUUID}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VetineoConfigurationError(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wymusza synchronizację głównych ustawień placówki z platformy Vetineo
   * do zewnętrznego systemu Kliniki XP.
   *
   * @param clinicUUID Unikalny identyfikator kliniki.
   * @returns Observable emitujący tablicę logów błędów w przypadku problemów.
   */
  setVetineoSettingsInKXP(clinicUUID: string): Observable<VetineoConfigurationError[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/set-vetineo-kxp-configuration/${clinicUUID}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VetineoConfigurationError(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }
}
